import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ViewContainerRef,
  ComponentRef,
  Type,
  inject,
  signal,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Meta } from "@angular/platform-browser";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { catchError, of } from "rxjs";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import {
  ExternalpageService,
  externalpageTypes,
  externalpageImageTypes,
} from "../../shared/services/externalpage.service";
import { ModelExternalPage } from "../../model/model.externalpage";
import { PageEventComponent } from "../page-events/page-event/page-event.component";
import { PageBlogPostComponent } from "../page-blog/page-blog-post/page-blog-post.component";
import { PageVideoComponent } from "../page-videos/page-video/page-video.component";
import { PageProjectComponent } from "../page-projects/page-project/page-project.component";
import { DomainService } from "../../shared/services/domain.service";
import { LocaleService } from "../../shared/services/locale.service";
import { HtmlMetaService } from "../../shared/services/html-meta.service";
import { LoaderComponent } from "../../core/loader/loader.component";
import { SafeHtmlPipe } from "../../pipe/pipe.safehtml";

@Component({
  selector: "app-page",
  standalone: true,
  imports: [CommonModule, TranslateModule, LoaderComponent, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="container-fluid bg-white">
      @if (isLoading()) {
        <app-loader></app-loader>
      }

      @if (!isLoading() && showGenericTemplate()) {
        <div class="page">
          <div class="page-body">
            <div class="container-section">
              <div class="container-smaller">
                <div class="content-wrapper">
                  <div class="content">
                    <div class="entity-single">
                      <div class="row">
                        <div class="column-left" [class]="hasImage() ? 'col-12 col-sm-7' : 'col-12'">
                          <h1 class="page-title entity-title alternate-heading">
                            {{ externalpageService.getTranslatedTitle(entity()) }}
                          </h1>
                          <span class="entity-content" [innerHTML]="externalpageService.getTranslatedContent(entity()) | safeHtml"></span>
                        </div>

                        @if (hasImage()) {
                          <div class="col-12 col-sm-5 column-right">
                            <div class="image-main entity-image-main">
                              <img class="img-fluid" [src]="entity().imageUri" />
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class PageComponent implements OnInit, OnDestroy {
  externalpageService = inject(ExternalpageService);
  private route = inject(ActivatedRoute);
  private viewContainer = inject(ViewContainerRef);
  private meta = inject(Meta);
  private domain = inject(DomainService);
  private locale = inject(LocaleService);
  private htmlMetaService = inject(HtmlMetaService);
  private destroyRef = inject(DestroyRef);

  externalpageTypes = externalpageTypes;

  isLoading = signal(false);
  showGenericTemplate = signal(false);
  entity = signal<ModelExternalPage>({
    id: "", status: "", type: "", title: "", titleEn: "", titleDe: "", titleFr: "", titlePt: "",
    content: "", contentEn: "", contentDe: "", contentFr: "", contentPt: "",
    permalink: "", regions: [], meetDayId: null, imageUri: "", videoUri: "",
    imageReferences: [], eventDate: "", eventPeriodDescription: "",
    testimonialName: "", testimonialType: "", testimonialOrganisation: "",
    metaDescription: "", metaDescriptionEn: null, metaDescriptionDe: null,
    metaDescriptionFr: null, metaDescriptionPt: null, metaKeywords: "",
    metaKeywordsEn: null, metaKeywordsDe: null, metaKeywordsFr: null, metaKeywordsPt: null,
  });

  private dynamicComponentRef: ComponentRef<any> | null = null;

  private componentMap = new Map<string, Type<any>>([
    [externalpageTypes.Event, PageEventComponent],
    [externalpageTypes.Blog, PageBlogPostComponent],
    [externalpageTypes.Video, PageVideoComponent],
    [externalpageTypes.Project, PageProjectComponent],
  ]);

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((routeParams) => {
        window.scrollTo(0, 0);
        const permalink = this.buildPermalink(routeParams['id']);
        this.loadPageData(permalink);
      });
  }

  private buildPermalink(id: string): string {
    const routeConfig = this.route.snapshot.routeConfig;
    if (routeConfig?.path === "pages/:id") return id;
    if (routeConfig?.path === ":id") return "~" + id;
    const currentPath = this.route.snapshot.url.map((s) => s.path).join("/");
    return currentPath.startsWith("pages/") ? id : "~" + id;
  }

  private loadPageData(permalink: string): void {
    this.isLoading.set(true);
    this.showGenericTemplate.set(false);
    this.clearDynamicComponent();

    const api$ = this.externalpageService.isGUID(permalink)
      ? this.externalpageService.apiGetExternalPage(permalink)
      : this.externalpageService.apiGetExternalPagePermalink(permalink);

    api$.pipe(
      catchError((err) => {
        console.error("Failed to load page:", err);
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((response) => {
      this.isLoading.set(false);

      if (!response) {
        this.showGenericTemplate.set(true);
        return;
      }

      const pageData = { ...response } as ModelExternalPage;
      this.parseImages(pageData);
      this.entity.set(pageData);
      this.updatePageTitle(pageData);

      const componentType = this.componentMap.get(pageData.type);
      if (componentType) {
        this.loadDynamicComponent(componentType, pageData);
      } else {
        this.showGenericTemplate.set(true);
        this.updateGenericMetaTags(pageData);
      }
    });
  }

  private loadDynamicComponent(componentType: Type<any>, pageData: ModelExternalPage): void {
    this.viewContainer.clear();
    this.dynamicComponentRef = this.viewContainer.createComponent(componentType);

    if (this.dynamicComponentRef.instance) {
      if ("entity" in this.dynamicComponentRef.instance) {
        this.dynamicComponentRef.instance.entity = { ...pageData };
      }
      if ("isLoading" in this.dynamicComponentRef.instance &&
          typeof this.dynamicComponentRef.instance.isLoading?.set === "function") {
        this.dynamicComponentRef.instance.isLoading.set(false);
      }
      if (typeof this.dynamicComponentRef.instance.parseImages === "function") {
        this.dynamicComponentRef.instance.parseImages();
      }
      if (typeof this.dynamicComponentRef.instance.loadCallback === "function") {
        this.dynamicComponentRef.instance.loadCallback();
      }
      this.dynamicComponentRef.changeDetectorRef.markForCheck();
    }
  }

  private clearDynamicComponent(): void {
    if (this.dynamicComponentRef) {
      this.dynamicComponentRef.destroy();
      this.dynamicComponentRef = null;
    }
    this.viewContainer.clear();
  }

  private parseImages(entity: ModelExternalPage): void {
    if (!entity?.imageReferences?.length) return;
    for (const image of entity.imageReferences) {
      if (image["imageType"] === externalpageImageTypes.MainImage) {
        entity.imageUri = image["imageUri"];
        break;
      }
    }
  }

  hasImage(): boolean {
    const e = this.entity();
    return !!(e?.imageUri && e.imageUri !== "");
  }

  private updatePageTitle(entity: ModelExternalPage): void {
    const translatedTitle = this.externalpageService.getTranslatedTitle(entity);
    if (translatedTitle) this.htmlMetaService.updateTitle(translatedTitle);
  }

  private updateGenericMetaTags(entity: ModelExternalPage): void {
    const suffix = this.getLocaleSuffix();
    const desc = (entity as any)[`metaDescription${suffix}`] ?? entity.metaDescription ?? "";
    const keywords = (entity as any)[`metaKeywords${suffix}`] ?? entity.metaKeywords ?? "";
    this.meta.updateTag({ name: "description", content: desc });
    this.meta.updateTag({ name: "keywords", content: keywords });
    this.meta.updateTag({ name: "og:image", content: entity.imageUri ?? "" });
  }

  private getLocaleSuffix(): string {
    const region = this.domain.getCurrentRegion();
    const language = this.locale.getCurrentLanguage();
    if (region === "BR" || language === "pt") return "Pt";
    if (region === "DE" || language === "de") return "De";
    if (region === "EU" || language === "en") return "En";
    return "";
  }

  ngOnDestroy(): void {
    this.clearDynamicComponent();
  }
}
